import "@awesome.me/webawesome/dist/styles/themes/default.css";
import "@awesome.me/webawesome/dist/components/details/details.js";
import "@awesome.me/webawesome/dist/components/drawer/drawer.js";
import type WaDrawer from "@awesome.me/webawesome/dist/components/drawer/drawer.js";
import "@awesome.me/webawesome/dist/components/tab-group/tab-group.js";
import "@awesome.me/webawesome/dist/components/tab/tab.js";
import "@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js";

import { AlertBanner } from "../components/alert-banner";
import { Burger } from "../components/burger";
import { LinkCard } from "../components/link-card";
import { Megamenu } from "../components/megamenu";
import { MegamenuItem } from "../components/megamenu-item";
import type { Route } from "../utils/router";
import { markExternalLinks } from "../utils/external-links";
import { addCandidateFilingSearch } from "../utils/candidate-filings";

export const common = {
	// JavaScript to be fired on all pages
	init() {
		// initialize custom elements
		if ("customElements" in globalThis) {
			safeDefineCustomElement("publica-alert-banner", AlertBanner);
			safeDefineCustomElement("publica-burger", Burger);
			safeDefineCustomElement("publica-link-card", LinkCard);
			safeDefineCustomElement("publica-megamenu", Megamenu);
			safeDefineCustomElement("publica-megamenu-item", MegamenuItem);

			initSearchDrawer();
			initVerticalTabGroups();
		}
	},

	// JavaScript to be fired on all pages, after page specific JS is fired
	finalize() {
		// Mark non-.gov links
		markExternalLinks();

		// Wire up search for any candidate filing embeds
		addCandidateFilingSearch();
	},
} satisfies Route;

/**
 * Define a custom element, but only if it has not yet been defined.
 * This avoids an error when redefining a component during hot reloading.
 */
function safeDefineCustomElement(
	...[name, ...constructorAndOptions]: Parameters<typeof customElements.define>
) {
	if (!customElements.get(name)) {
		customElements.define(name, ...constructorAndOptions);
	}
}

function initSearchDrawer() {
	const drawer: WaDrawer | null = document.querySelector("#search-drawer");

	if (drawer == null) return;

	const openButton = drawer?.nextElementSibling;

	openButton?.addEventListener("click", () => (drawer.open = true));

	const searchInput = drawer.querySelector("input[type='search']");

	if (searchInput?.classList.contains("swp-input--search")) {
		enableSearchWPLiveResultsInDrawer({ drawer, searchWPInput: searchInput });
	}
}

/**
 * There's a problem with using the SearchWP Live Results in the Search Drawer.
 * The drawer uses a native dialog, which is rendered in the special "top layer."
 * The Live Results container is inserted into the `body` and positioned with z-index,
 * but without being in a dialog or a popover, it can never render above the dialog,
 * which means it's never visible even when active.
 *
 * So we have to look for any SearchWP Live Results containers and try moving them
 * into the Search Drawer.
 */
function enableSearchWPLiveResultsInDrawer({
	drawer,
	searchWPInput,
}: {
	drawer: WaDrawer;
	searchWPInput: Element;
}) {
	const searchWPLiveResultsContainerId =
		searchWPInput.getAttribute("aria-owns");
	const searchWPLiveResultsContainer =
		searchWPLiveResultsContainerId == null
			? null
			: document.querySelector(`#${searchWPLiveResultsContainerId}`);

	if (searchWPLiveResultsContainer == null) {
		// If we don't have a Live Results container, the plugin's JS might not have run yet
		// Watch the body for new children, and initialize our stuff if we see that
		// a Live Results container has been added
		const observer = new MutationObserver((mutationList) => {
			for (const mutation of mutationList) {
				if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
					for (const [, node] of mutation.addedNodes.entries()) {
						if (node.nodeType === node.ELEMENT_NODE) {
							const element = node as Element;
							// const nodeClasslist = (node as Element).classList;
							if (element.classList.contains("searchwp-live-search-results")) {
								moveSearchWPLiveResultsIntoDrawer({
									drawer,
									searchWPInput,
									searchWPLiveResultsContainer: element,
								});
							}
						}
					}
				}
			}
		});

		observer.observe(document.body, { childList: true });
	} else {
		moveSearchWPLiveResultsIntoDrawer({
			drawer,
			searchWPInput,
			searchWPLiveResultsContainer,
		});
	}
}

/**
 * Move the given SearchWP Live Results container into the search drawer.
 * First we need to check the id against the `aria-owns` id on the drawer's
 * search input. If it doesn't match, it might be the container for another
 * form on the page.
 */
function moveSearchWPLiveResultsIntoDrawer({
	drawer,
	searchWPInput,
	searchWPLiveResultsContainer,
}: {
	searchWPInput: Element;
	drawer: WaDrawer;
	searchWPLiveResultsContainer: Element;
}) {
	const searchWPInputLiveResultsContainerId =
		searchWPInput.getAttribute("aria-owns");
	const resultsContainerId = searchWPLiveResultsContainer.getAttribute("id");

	if (
		searchWPInputLiveResultsContainerId == null ||
		resultsContainerId == null ||
		searchWPInputLiveResultsContainerId !== resultsContainerId
	) {
		return;
	}

	drawer.append(searchWPLiveResultsContainer);
}

/**
 * In pixels.
 * Tabs should always be stacked below this (exclusive), and vertical above (inclusive).
 */
const VERTICAL_TABS_MIN_BREAKPOINT = 768;

/**
 * Vertical tabs only work well on desktop.
 * Change the placement from "start" to "top" on mobile.
 */
function initVerticalTabGroups() {
	const verticalTabGroups = document.querySelectorAll(
		"wa-tab-group[placement='start']",
	);

	const media = `(width >= ${VERTICAL_TABS_MIN_BREAKPOINT}px)`;
	const mql = globalThis.matchMedia(media);

	mql.addEventListener("change", (event) => {
		for (const tabGroup of verticalTabGroups) {
			tabGroup.setAttribute("placement", event.matches ? "start" : "top");
		}
	});

	// Trigger change on load
	mql.dispatchEvent(
		new MediaQueryListEvent("change", {
			media,
			matches: document.body.clientWidth >= VERTICAL_TABS_MIN_BREAKPOINT,
		}),
	);
}
